import type { History } from 'history'
import React, { useCallback, useEffect, useState } from 'react'

import { useAppContext } from '@/app-context'
import Dialog from '@/components/molecules/dialog'
import Radio from '@/components/ui/radio'
import { useToast } from '@/components/ui/toast'
import { generateInstitutionToken } from '@/services/auth'
import type { InstitutionDetail, InstitutionItem } from '@/types/institution'
import { getErrorMessage } from '@/utils/error'
import { getSession, storeSession } from '@/utils/session'

type DialogChangeInstitutionProps = {
  isOpen: boolean
  onClose: () => void
  history: History
}

const DialogChangeInstitution = ({
  isOpen,
  onClose,
  history
}: DialogChangeInstitutionProps) => {
  const { institutions, currentInstitution, setCurrentInstitution } =
    useAppContext()
  const toast = useToast()
  const [selectedInstitution, setSelectedInstitution] =
    useState<InstitutionItem | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setSelectedInstitution(
        institutions.find(
          (i) => i.investor_id === currentInstitution?.investor_id
        ) ?? null
      )
    }
  }, [isOpen, institutions, currentInstitution?.investor_id])

  const handleFetchInstitutionToken = useCallback(
    async (data: InstitutionItem) => {
      const session = getSession('b-bfb')
      storeSession('b-bfb', { ...session, ...data }, { value: 25 })
      const tokenResponse = await generateInstitutionToken()
      const tokenData = tokenResponse?.data?.data
      if (!tokenData) {
        throw new Error(
          getErrorMessage(tokenResponse) || 'Gagal mendapatkan token institusi'
        )
      }
      const finalSession = {
        ...session,
        ...data,
        ...tokenData,
        token: window.btoa(tokenData.local_token as string),
        refresh_token: window.btoa(tokenData.refresh_token as string)
      }
      storeSession('b-bfb', finalSession, { value: 25 })
      setCurrentInstitution(finalSession as InstitutionDetail)
      onClose()
      history.replace('/dashboard')
    },
    [history, onClose, setCurrentInstitution]
  )

  const handleContinue = async () => {
    if (!selectedInstitution) return

    setIsLoading(true)
    try {
      await handleFetchInstitutionToken(selectedInstitution)
    } catch (error) {
      const message = getErrorMessage(error)
      toast({
        description: message || 'Terjadi kesalahan saat memproses institusi'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="md">
      <Dialog.Header>
        <Dialog.Title>Pilih Profil Institusi</Dialog.Title>
        <Dialog.Close />
      </Dialog.Header>

      <Dialog.Content>
        <p className="body-sm-regular text-neutral-gray-500 mb-4">
          Anda memiliki akses ke{' '}
          <span className="body-sm-medium text-neutral-gray-700">
            {institutions.length} institusi
          </span>
          :
        </p>

        <div className="space-y-3">
          {institutions.map((institution) => {
            const isSelected =
              selectedInstitution?.investor_id === institution.investor_id
            return (
              <div
                key={institution.investor_id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedInstitution(institution)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    setSelectedInstitution(institution)
                }}
                className={`w-full flex items-center gap-2 p-4 rounded-xl border shadow-low bg-flat-white cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-biome-green-500 bg-biome-green-50 border-2'
                    : 'border-neutral-gray-50 hover:bg-neutral-gray-25'
                }`}
                data-test-id={`change_institution_card_option_${institution.investor_id}`}
              >
                <Radio
                  isChecked={isSelected}
                  name="change-institution"
                  value={String(institution.investor_id)}
                  onChange={() => setSelectedInstitution(institution)}
                  onClick={(e) => e.stopPropagation()}
                  data-test-id={`change_institution_radio_option_${institution.investor_id}`}
                />
                <p
                  className={`flex-1 subheading-m-regular ${
                    isSelected
                      ? 'text-biome-green-700'
                      : 'text-neutral-gray-800'
                  }`}
                >
                  {institution.institution_name}
                </p>
              </div>
            )
          })}
        </div>
      </Dialog.Content>

      <Dialog.Actions>
        <button
          type="button"
          disabled={!selectedInstitution || isLoading}
          onClick={handleContinue}
          className="button button-sm button-primary w-full"
          data-test-id="change_institution_button_confirm"
        >
          {isLoading ? (
            <span className="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : (
            'Pilih Institusi'
          )}
        </button>
      </Dialog.Actions>
    </Dialog>
  )
}

export default DialogChangeInstitution
